<template>
  <div class="attendance-notification-settings">
    <!-- Reset Notifications Button -->
    <div class="flex justify-end my-4">
      <UIButton button="primary" size="lg" @click="resetNotifications">
        Reset Notifications
      </UIButton>
    </div>
    <!-- Reports Buttons -->
    <div v-if="reports?.length" v-permission="TEMPLATES_PERMISSIONS" class="mb-6 flex justify-end">
      <div class="text-right">
        <div class="flex flex-wrap gap-3 justify-end">
          <div v-for="(report, index) in reports" :key="index" class="inline-flex items-center">
            <UIButton
              button="primary"
              size="md"
              class="capitalize"
              @click="previewReportHandler(report)"
            >
              {{ reportTitle(report) }}
            </UIButton>
          </div>
        </div>
      </div>
    </div>
    <!-- Notifications Table -->
    <Loader v-if="isLoading" class="mt-20 mb-40" :content="true" />
    <NoRecordFound v-else-if="!isLoading && isEmpty(notificationsList)" />
    <!-- Debug info -->
    <div v-for="action in notificationsList" v-else :key="action.action" class="mb-6">
      <div class="notification-header">
        <h3 class="text-lg font-semibold mb-4">Alerts For {{ action?.action }}</h3>
      </div>
      <NoRecordFound v-if="isEmpty(action.triggers)" />
      <TableWrapper v-else table-height="h-auto">
        <THead>
          <TRHead>
            <TH v-for="(header, index) in tableHeaders" :key="index">
              {{ header }}
            </TH>
            <TH v-permission="TEMPLATES_PERMISSIONS">Templates</TH>
          </TRHead>
        </THead>
        <TBody>
          <TRBody v-for="(notification, index) in action.triggers" :key="index">
            <TD>
              <div class="flex items-center justify-between">
                <span class="entity-name font-medium">
                  {{ transformTextToCapitalize(notification.send_to) }}
                </span>
                <span class="ml-2.5">
                  <UiLockAnimation
                    :model-value="notification.is_locked"
                    :deactivate="false"
                    @update:modelValue="toggleNotification(notification, 'is_locked')"
                  />
                </span>
              </div>
            </TD>
            <TD>
              <div v-if="getNotificationReceivers(notification.send_to)">
                <div class="divide-y">
                  <div
                    v-for="(receiver, receiverIndex) in notification.user_receivers"
                    :key="receiverIndex"
                    class="py-1 flex items-center justify-between"
                  >
                    <MultiUserDisplay
                      :options="addReciversFullName(receiver)"
                      label="full_name"
                      :image="true"
                      :value="medium"
                      :dropdown-top-bottom="
                        index + 1 === (action.triggers?.length || 0) ? 'bottom-6' : 'top-6'
                      "
                      :tooltip="
                        index + 1 === (action.triggers?.length || 0)
                          ? 'tooltip-bottom'
                          : 'tooltip-top'
                      "
                    />
                    <div
                      class="cursor-pointer"
                      @click="
                        toggleUnScheduleModel(
                          notification,
                          { action: action.action },
                          receiverIndex,
                        )
                      "
                    >
                      <Icon icon="edit" width="16" height="16" class="ml-2.5" />
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="flex items-center justify-center text-4xl font-bold">----</div>
            </TD>

            <TD>
              <div class="notification-options divide-y">
                <div
                  v-for="medium in sendMediums"
                  :key="medium"
                  class="option-item flex items-center py-2"
                >
                  <UiCheckBox
                    :model-value="notification?.mediums?.includes(medium)"
                    :halt-update="notification.is_locked"
                    @update:modelValue="toggleMedium(notification, medium, $event)"
                  />

                  <span class="ml-3">{{ medium.toUpperCase() }}</span>
                </div>
              </div>
            </TD>
            <TD>
              <div class="dont-send-options divide-y">
                <div
                  v-for="medium in dontSendIfMediums"
                  :key="medium"
                  class="option-item flex items-center py-2"
                >
                  <UiCheckBox
                    v-if="medium !== COMMUNICATION_MEDIUMS.WEBPUSH"
                    :model-value="notification?.dont_send_if_sent?.includes(medium)"
                    :halt-update="notification.is_locked"
                    @update:modelValue="toggleDontSend(notification, medium, $event)"
                  />

                  <span v-if="medium !== COMMUNICATION_MEDIUMS.WEBPUSH" class="ml-3 lowercase">
                    <span class="uppercase">{{ medium }}</span>
                    was sent
                  </span>
                  <span v-else>
                    {{ GENERAL_CONSTANTS.NOT_APPLICABLE }}
                  </span>
                </div>
              </div>
            </TD>
            <TD v-permission="TEMPLATES_PERMISSIONS">
              <div class="template-preview-options divide-y">
                <div class="option-item flex items-center py-2">
                  <span class="flex-1">Email</span>
                  <icon
                    class="icon cursor-pointer ml-2"
                    icon="eye"
                    color="primary-gray-400"
                    height="17.5"
                    width="21.31"
                    @click="previewTemplate(notification, COMMUNICATION_MEDIUMS.EMAIL)"
                  />
                </div>
                <div class="option-item flex items-center py-2">
                  <span class="flex-1">Webpush</span>
                  <icon
                    class="icon cursor-pointer ml-2"
                    icon="eye"
                    color="primary-gray-400"
                    height="17.5"
                    width="21.31"
                    @click="previewTemplate(notification, COMMUNICATION_MEDIUMS.WEBPUSH)"
                  />
                </div>
                <div class="option-item flex items-center py-2">
                  <span class="flex-1">In App</span>
                  <icon
                    class="icon cursor-pointer ml-2"
                    icon="eye"
                    color="primary-gray-400"
                    height="17.5"
                    width="21.31"
                    @click="previewTemplate(notification, COMMUNICATION_MEDIUMS.SOCKET)"
                  />
                </div>
              </div>
            </TD>
          </TRBody>
        </TBody>
      </TableWrapper>
    </div>

    <!-- Preview Template Modal -->
    <PreviewTemplateModal
      v-if="showPreviewTemplateModal"
      :modal="showPreviewTemplateModal"
      :preview-html="previewHtml"
      :is-loading="isReportViewLoading"
      :current-medium="currentMedium"
      @toggle="toggleReportModal"
    />
    <!-- Update Notification Receiver Modal -->
    <updateNotificationReceiverModal
      v-if="receiverUpdateModel"
      :show-model="receiverUpdateModel"
      :edit="currentNotificationObject || {}"
      :receiver-index="excludedReceiverIndex"
      @update="updateNotificationReceiverModal"
    />
  </div>
</template>

<script>
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import UiLockAnimation from '@src/components/UiElements/UiLockAnimation.vue'
import UiCheckBox from '@src/components/UiElements/UiCheckbox.vue'
import PreviewTemplateModal from '@src/components/Settings/notifications/PreviewTemplateModal.vue'
import { mapActions, mapState } from 'vuex'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import { ENV } from '@src/constants/general-constants.js'
import { transformTextToCapitalize } from '@src/utils/generalUtil.js'
import MultiUserDisplay from '@src/components/UiElements/MultiUserDisplay.vue'
import updateNotificationReceiverModal from '@/src/components/Settings/notifications/updateNotificationReceiverModal.vue'
import icon from '@components/icons/icon.vue'
import { fullName } from '@src/utils/settings/tenant-user.util.js'
import { isArray, isEmpty } from 'lodash'
import Loader from '@components/BaseComponent/Loader.vue'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import GENERAL_CONSTANTS from '@src/constants/general-constants.js'
import {
  NOTIFICATION_RECIEVERS,
  COMMUNICATION_MEDIUMS,
} from '@src/constants/notification-constants.js'
export default {
  name: 'NotificationReminderContainer',
  components: {
    TableWrapper,
    TBody,
    TD,
    TH,
    THead,
    TRBody,
    TRHead,
    UiLockAnimation,
    UiCheckBox,
    PreviewTemplateModal,
    MultiUserDisplay,
    icon,
    updateNotificationReceiverModal,
    Loader,
    NoRecordFound,
    UIButton,
  },
  props: {
    reports: {
      type: Array,
      default: () => [],
    },
    featureName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      COMMUNICATION_MEDIUMS,
      GENERAL_CONSTANTS,
      dontSendIfMediums: [
        COMMUNICATION_MEDIUMS.EMAIL,
        COMMUNICATION_MEDIUMS.SMS,
        COMMUNICATION_MEDIUMS.WEBPUSH,
      ],
      sendMediums: [
        COMMUNICATION_MEDIUMS.SMS,
        COMMUNICATION_MEDIUMS.EMAIL,
        COMMUNICATION_MEDIUMS.WEBPUSH,
      ],
      previewHtml: null,
      showPreviewTemplateModal: false,
      isReportViewLoading: false,
      TEMPLATES_PERMISSIONS: { env: [ENV.DEV] },
      receiverUpdateModel: false,
      currentActionObject: null,
      currentNotificationObject: null,
      notificationsList: [],
      isLoading: false,
      excludedReceiverIndex: null,
      currentMedium: null,
    }
  },
  computed: {
    ...mapState('layout', ['currentCampusScope']),
    tableHeaders() {
      return ['Notify', 'Receivers', 'Send', "Don't Send If"]
    },
    reportTitle() {
      return (report) => `${report} Report`
    },
  },
  watch: {
    currentCampusScope: {
      handler() {
        this.loadNotifications()
      },
      deep: true,
    },
  },
  created() {
    this.loadNotifications()
  },
  methods: {
    fullName,
    isEmpty,
    formatAction(action) {
      if (!action || typeof action !== 'string') return ''
      return action
        .replace(/^ACTION_/, '')
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },
    updateNotificationReceiverModal(emitValue) {
      this.receiverUpdateModel = !this.receiverUpdateModel
      if (emitValue === 'close') {
        return
      }
      this.loadNotifications()
    },
    async loadNotifications() {
      const payload = { feature: this.featureName }
      this.isLoading = true

      try {
        const result = await this.getNotificationTriggers(payload)
        const res = isArray(result) ? result[0] : result
        const records = res?.data?.records || []
        const uniqueSendToValues = [
          ...new Set(records.map((record) => record.send_to).filter(Boolean)),
        ]
        this.currentActionObject = {
          optionsList: uniqueSendToValues.map((sendTo) => ({
            value: sendTo,
            name: this.transformTextToCapitalize(sendTo),
            label: this.transformTextToCapitalize(sendTo),
          })),
        }
        const groupedByAction = this.groupRecordsByAction(records)
        const mergedBySendTo = groupedByAction.map((group) => ({
          action: this.formatAction(group.action),
          triggers: this.mergeTriggersBySendTo(group.triggers),
        }))
        this.notificationsList = mergedBySendTo
      } finally {
        this.isLoading = false
      }
    },

    groupRecordsByAction(records) {
      return Object.values(
        records.reduce((acc, item) => {
          const action = item.action || 'unknown'
          if (!acc[action]) {
            acc[action] = { action, triggers: [] }
          }
          acc[action].triggers.push(item)
          return acc
        }, {}),
      )
    },

    mergeTriggersBySendTo(triggers) {
      const merged = {}
      const mediumOrder = [
        COMMUNICATION_MEDIUMS.SMS,
        COMMUNICATION_MEDIUMS.EMAIL,
        COMMUNICATION_MEDIUMS.WEBPUSH,
      ]

      triggers.forEach((trigger) => {
        const sendToKey = trigger.send_to || 'unknown'

        if (!merged[sendToKey]) {
          merged[sendToKey] = {
            ...trigger,
            mediums: Array.isArray(trigger.mediums) ? [...trigger.mediums] : [],
            dont_send_if_sent: Array.isArray(trigger.dont_send_if_sent)
              ? [...trigger.dont_send_if_sent]
              : [],
            user_receivers: [], // This will be ordered by medium
          }
        }

        const target = merged[sendToKey]

        if (trigger.medium && !target?.mediums?.includes(trigger.medium)) {
          target.mediums.push(trigger.medium)
        }

        if (Array.isArray(trigger.dont_send_if_sent)) {
          trigger.dont_send_if_sent.forEach((medium) => {
            if (!target.dont_send_if_sent?.includes(medium)) {
              target.dont_send_if_sent.push(medium)
            }
          })
        }
        if (target.user_receivers.length === 0) {
          target.user_receivers = mediumOrder.map(() => [])
        }
        if (
          trigger.notification_trigger_receivers &&
          Array.isArray(trigger.notification_trigger_receivers)
        ) {
          const mediumIndex = mediumOrder.indexOf(trigger.medium)
          if (mediumIndex !== -1) {
            target.user_receivers[mediumIndex] = trigger.notification_trigger_receivers
          }
        }
      })
      return Object.values(merged)
    },

    addNotification(data) {
      return addNotificationUtil(this, data)
    },
    transformTextToCapitalize,

    toggleNotification(notification, field) {
      if (field === 'enabled') {
        if (notification.disabled_at) {
          notification.disabled_at = null
        } else {
          notification.disabled_at = new Date().toISOString()
        }
      } else if (field === 'is_locked') {
        notification.is_locked = !notification.is_locked
      }
    },

    async toggleMedium(notification, medium, currentValue) {
      if (!notification.mediums) {
        notification.mediums = []
      }

      const payload = {
        feature: this.featureName,
        action: notification.action,
        send_to: notification.send_to,
        medium: medium,
        receiver_role_id: notification.receiver_role_id,
      }

      if (currentValue) {
        await this.enableInstantNotificationTrigger(payload).then(() => {
          this.loadNotifications()
        })
      } else {
        await this.disableInstantNotificationTrigger(payload).then(() => {
          this.loadNotifications()
        })
      }
    },

    async toggleDontSend(notification, medium, currentValue) {
      const payload = {
        feature: this.featureName,
        action: notification.action,
        send_to: notification.send_to,
        medium: medium,
        receiver_role_id: notification.receiver_role_id,
        dont_send_if_sent: currentValue ? [medium] : [],
      }
      await this.updateDontSendIf(payload).then(() => {
        this.loadNotifications()
      })
    },
    toggleUnScheduleModel(notification, actionData, index) {
      this.excludedReceiverIndex = index !== undefined ? index : null
      this.currentNotificationObject = { ...notification, mediums: this.sendMediums } || null
      this.currentActionObject = actionData || null
      this.receiverUpdateModel = !this.receiverUpdateModel
    },
    previewReportHandler(report) {
      this.showPreviewTemplateModal = true
      const payload = {
        role: TENANT_ROLES.SUPER_ADMIN,
        feature: this.featureName,
        action: `${report}_report`,
        medium: COMMUNICATION_MEDIUMS.EMAIL,
      }
      this.isReportViewLoading = true
      this.getNotificationPreview(payload)
        .then((data) => {
          this.previewHtml = data || { title: '', body: '' }
        })
        .finally(() => {
          this.isReportViewLoading = false
        })
    },
    previewTemplate(notification, medium) {
      this.showPreviewTemplateModal = true
      this.currentMedium = medium
      const payload = {
        role: notification.receiver_role_id,
        feature: this.featureName,
        action: notification.action,
        medium: medium === COMMUNICATION_MEDIUMS.SOCKET ? COMMUNICATION_MEDIUMS.WEBPUSH : medium,
        send_to: notification.send_to,
      }
      this.isReportViewLoading = true
      this.getNotificationPreview(payload)
        .then((data) => {
          this.previewHtml = data || { title: '', body: '' }
        })
        .finally(() => {
          this.isReportViewLoading = false
        })
    },
    toggleReportModal() {
      this.showPreviewTemplateModal = !this.showPreviewTemplateModal
      if (!this.showPreviewTemplateModal) {
        this.previewHtml = null
        this.currentMedium = null
      }
    },
    addReciversFullName(notification_trigger_receivers) {
      if (!isArray(notification_trigger_receivers)) {
        return []
      }
      return notification_trigger_receivers
        .map((user) => (user ? user.receiver : null))
        .filter(Boolean)
        .map((r) => {
          const receiver = {
            ...r,
          }
          this.fullName(receiver)
          return receiver
        })
    },

    async resetNotifications() {
      const payload = {
        feature: this.featureName,
      }
      await this.resetAllNotifications(payload).then(() => {
        this.loadNotifications()
      })
    },

    getNotificationReceivers(send_to) {
      return send_to === NOTIFICATION_RECIEVERS.SPECIFIC_STAFF
    },
    ...mapActions('notifications', [
      'getNotificationPreview',
      'getNotificationTriggers',
      'disableInstantNotificationTrigger',
      'enableInstantNotificationTrigger',
      'updateDontSendIf',
      'resetAllNotifications',
    ]),
  },
}
</script>
